import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { usePermissions } from '../hooks/usePermissions';
import { List, Kanban, Zap, Plus, Users, Paperclip, GanttChart } from 'lucide-react';
import KanbanBoard from '../components/Board/KanbanBoard';
import TaskListView from '../components/Tasks/TaskListView';
import SprintView from '../components/Tasks/SprintView';
import FilesView from '../components/Files/FilesView';
import GanttView from '../components/Gantt/GanttView';
import CreateTaskModal from '../components/Tasks/CreateTaskModal';
import MembersPanel from '../components/Projects/MembersPanel';
import { AvatarGroup } from '../components/UI/Avatar';

const VIEWS = [
  { id: 'board', label: 'Доска', icon: Kanban },
  { id: 'list', label: 'Список', icon: List },
  { id: 'sprint', label: 'Спринты', icon: Zap },
  { id: 'gantt', label: 'Гант', icon: GanttChart },
  { id: 'files', label: 'Файлы', icon: Paperclip },
];

export default function ProjectPage() {
  const { id, view = 'list' } = useParams();
  const navigate = useNavigate();
  const { activeProject, fetchTasks, fetchSprints, fetchBoards, setActiveProject, projects } = useStore();
  const { canCreateTask, canProjectManageMembers } = usePermissions();
  const [showCreate, setShowCreate] = useState(false);
  const [showMembers, setShowMembers] = useState(false);

  useEffect(() => {
    if (id) {
      const p = projects.find(x => x._id === id);
      if (p) setActiveProject(p);
      fetchTasks({ project: id });
      fetchSprints(id);
      fetchBoards(id);
    }
  }, [id]);

  if (!activeProject) return (
    <div className="flex-1 flex items-center justify-center">
      <div className="animate-spin rounded-full h-6 w-6 border-2 border-brand-500 border-t-transparent" />
    </div>
  );

  const setView = v => navigate(`/project/${id}/${v}`);
  const memberUsers = (activeProject.members || []).map(m => m.user).filter(Boolean);
  const isFilesView = view === 'files';

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex-shrink-0 flex-wrap gap-y-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-base flex-shrink-0"
          style={{ background: (activeProject.color || '#4f63ff') + '20' }}>
          {activeProject.icon || '📋'}
        </div>
        <h1 className="font-semibold truncate max-w-[120px] sm:max-w-xs">{activeProject.name}</h1>
        <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded font-mono hidden sm:inline">{activeProject.key}</span>

        <div className="flex-1" />

        {/* Members — desktop */}
        {canProjectManageMembers() && (
          <button onClick={() => setShowMembers(true)}
            className="hidden sm:flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group">
            <AvatarGroup users={memberUsers} max={4} />
            <span className="text-xs text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300 flex items-center gap-1">
              <Users className="w-3 h-3" />{memberUsers.length}
            </span>
          </button>
        )}
        {!canProjectManageMembers() && (
          <div title="Требуется роль администратора" className="hidden sm:flex items-center gap-2 px-2.5 py-1.5 text-gray-400 cursor-not-allowed">
            <AvatarGroup users={memberUsers} max={4} />
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Users className="w-3 h-3" />{memberUsers.length}
            </span>
          </div>
        )}

        {/* View tabs — scrollable on mobile */}
        <div className="w-full sm:w-auto order-last sm:order-none overflow-x-auto">
          <div className="flex gap-0.5 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 w-max min-w-full sm:min-w-0">
            {VIEWS.map(v => {
              const Icon = v.icon;
              const active = view === v.id || (!view && v.id === 'board');
              return (
                <button key={v.id} onClick={() => setView(v.id)}
                  className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap
                    ${active ? 'bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  <span className="hidden xs:inline sm:inline">{v.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {!isFilesView && canCreateTask() && (
          <button onClick={() => setShowCreate(true)} className="btn-primary py-1.5 px-3 text-xs sm:text-sm">
            <Plus className="w-4 h-4" />
            <span className="hidden xs:inline">Задача</span>
          </button>
        )}
        {!isFilesView && !canCreateTask() && (
          <button disabled title="Требуется роль участника или выше" className="btn-primary py-1.5 px-3 text-xs sm:text-sm opacity-50 cursor-not-allowed">
            <Plus classNamменюe="w-4 h-4" />
            <span className="hidden xs:inline">Задача</span>
          </button>
        )}
      </div>

      {/* View content */}
      <div className="flex-1 overflow-hidden">
        {(view === 'board' || !view) && <KanbanBoard />}
        {view === 'list' && <TaskListView />}
        {view === 'sprint' && <SprintView />}
        {view === 'gantt' && <GanttView />}
        {view === 'files' && <FilesView />}
      </div>

      {showCreate && <CreateTaskModal onClose={() => setShowCreate(false)} />}
      {showMembers && <MembersPanel project={activeProject} onClose={() => setShowMembers(false)} />}
    </div>
  );
}
